import React, { useState, useEffect } from 'react';
import CustomerField from '../CustomerField';
import { toast } from 'react-toastify';

const generateTimes = () => {
  const times = [];
  for (let h = 9; h <= 21; h++) {
    times.push(`${String(h).padStart(2, '0')}:00`);
    times.push(`${String(h).padStart(2, '0')}:30`);
  }
  return times;
};

/* ─── tiny style helpers ─── */
const field = {
  width: '100%',
  border: '1.5px solid #E8E6F0',
  borderRadius: '10px',
  padding: '10px 14px',
  fontSize: '14px',
  background: '#FAFAFA',
  color: '#1A1A2E',
  outline: 'none',
  transition: 'border-color 0.15s',
  fontFamily: 'inherit',
};
const fieldFocus = { borderColor: '#7C6FCD', background: '#fff' };
const label = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '6px',
};

function FocusField({ style: extraStyle, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{ ...field, ...(focused ? fieldFocus : {}), ...extraStyle }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

function FocusSelect({ children, style: extraStyle, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      style={{ ...field, ...(focused ? fieldFocus : {}), ...extraStyle, cursor: 'pointer' }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
}

const STATUS_CONFIG = {
  '': { label: 'Ολοκληρωμένο', color: '#15803d', bg: '#f0fdf4', dot: '#22c55e' },
  cancelled: { label: 'Ακυρώθηκε', color: '#b91c1c', bg: '#fef2f2', dot: '#ef4444' },
  no_show: { label: 'Δεν ήρθε', color: '#b45309', bg: '#fffbeb', dot: '#f59e0b' },
};

/* Base URL for the FastAPI backend (python.remindly.gr). Adjust here if your
   actual public path differs (e.g. proxied behind a prefix). */
const PY_API_BASE = 'https://python.remindly.gr';

const AppointmentModal = ({
  isOpen,
  onClose,
  onSaved,
  slotInfo,
  employee,
  appointment = null,
  appointments = [],
  setSelectedAppointment,
}) => {
  const [customer, setCustomer] = useState(null);
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timeError, setTimeError] = useState('');
  const [sendSms, setSendSms] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!startTime || !endTime) { setTimeError(''); return; }
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    if (eh * 60 + em <= sh * 60 + sm) {
      setTimeError('Η ώρα λήξης πρέπει να είναι μετά την ώρα έναρξης.');
    } else {
      setTimeError('');
    }
  }, [startTime, endTime]);

  useEffect(() => {
    if (!isOpen) return;
    setServiceId(''); setStartTime(''); setEndTime(''); setStatus(''); setSaving(false);

    // Fixed: was hitting the old PHP endpoint (api.remindly.gr/get-services.php),
    // which is why the dropdown never had anything in it. This now calls the
    // real FastAPI /services route. Filtering by "active" is now done
    // server-side (see backend fix), so we just trust what comes back here —
    // the old client-side `s.active === 1 || s.active === '1'` check never
    // matched anyway, since SQLAlchemy Booleans serialize as true/false, not 1/'1'.
    fetch(`${PY_API_BASE}/services`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setServices(Array.isArray(data) ? data : []))
      .catch(() => setServices([]));

    const baseStart = appointment ? new Date(appointment.start) : new Date(slotInfo?.start);
    const baseEnd = appointment ? new Date(appointment.end) : new Date(baseStart.getTime() + 30 * 60000);
    const pad = n => String(n).padStart(2, '0');

    setStartTime(`${pad(baseStart.getHours())}:${pad(baseStart.getMinutes())}`);
    setEndTime(`${pad(baseEnd.getHours())}:${pad(baseEnd.getMinutes())}`);

    if (appointment) {
      setCustomer({ id: appointment.customer_id, name: appointment.customer });
      setServiceId(appointment.service_id || '');
      setSendSms(Boolean(appointment.send_sms));
      setSendEmail(Boolean(appointment.send_email));
      setStatus(appointment.status || '');
    } else {
      setCustomer(null); setSendSms(false); setSendEmail(false);
    }
  }, [isOpen, slotInfo, employee, appointment]);

  const formatDateTime = (baseDate, timeStr) => {
    const [h, m] = timeStr.split(':');
    const d = new Date(baseDate);
    d.setHours(Number(h)); d.setMinutes(Number(m)); d.setSeconds(0);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  };

  const handleSave = async () => {
    if (!customer || !serviceId || !startTime || !endTime) {
      toast.error('Συμπλήρωσε όλα τα πεδία.'); return;
    }
    if (timeError) { toast.error(timeError); return; }
    setSaving(true);

    const baseDate = appointment ? new Date(appointment.start) : new Date(slotInfo.start);
    const payload = {
      employee_id: employee.id,
      customer_id: customer.id,
      service_id: serviceId,
      start_time: formatDateTime(baseDate, startTime),
      end_time: formatDateTime(baseDate, endTime),
      wants_sms: sendSms ? 1 : 0,
      wants_email: sendEmail ? 1 : 0,
      status: status || null,
    };
    if (appointment) payload.id = appointment.id;

    const url = appointment
      ? 'https://api.remindly.gr/update-appointment.php'
      : 'https://api.remindly.gr/add-appointment.php';

    try {
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.success) {
        toast.success(`Ραντεβού ${appointment ? 'ενημερώθηκε' : 'καταχωρήθηκε'}!`);
        await fetch('https://api.remindly.gr/update-customer-preferences.php', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer_id: customer.id, wants_sms: sendSms, wants_email: sendEmail }),
        });
        onSaved(); onClose();
      } else {
        toast.error('Σφάλμα κατά την αποθήκευση');
      }
    } catch { toast.error('Σφάλμα δικτύου'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!appointment) return;
    if (!window.confirm('Θες σίγουρα να διαγράψεις το ραντεβού;')) return;
    try {
      const res = await fetch('https://api.remindly.gr/delete-appointment.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: appointment.id }),
      });
      const data = await res.json();
      if (data.success) { toast.success('Ραντεβού διαγράφηκε.'); onSaved(); onClose(); }
      else toast.error('Σφάλμα κατά τη διαγραφή');
    } catch { toast.error('Σφάλμα δικτύου'); }
  };

  if (!isOpen) return null;
  const hours = generateTimes();
  const isEdit = Boolean(appointment);
  const empColor = employee?.color || '#7C6FCD';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(16,16,42,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
        padding: '16px',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '480px',
          boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        {/* Header — corners rounded explicitly since the card no longer clips
            overflow (that clipping was cutting off CustomerField's "new
            customer" popup: the email field, SMS/Email toggles, and the
            save/cancel buttons were being sliced off whenever that popup grew
            taller than the space left in the card). */}
        <div style={{ background: empColor, padding: '20px 24px 18px', position: 'relative', borderRadius: '20px 20px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {isEdit ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              )}
            </div>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {isEdit ? 'Επεξεργασία' : 'Νέο Ραντεβού'}
              </div>
              <div style={{ color: '#fff', fontSize: '16px', fontWeight: 700 }}>
                {employee ? employee.name : ''}
              </div>
            </div>
          </div>
          {/* Close btn */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              width: '30px', height: '30px', borderRadius: '8px',
              background: 'rgba(255,255,255,0.2)', border: 'none',
              color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          {/* Time row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '18px' }}>
            <div>
              <label style={label}>Ώρα Έναρξης</label>
              <FocusField type="search" list="appt-hours" value={startTime} onChange={e => setStartTime(e.target.value)} placeholder="09:00" />
            </div>
            <div>
              <label style={label}>Ώρα Λήξης</label>
              <FocusField type="search" list="appt-hours" value={endTime} onChange={e => setEndTime(e.target.value)} placeholder="09:30" />
            </div>
          </div>
          <datalist id="appt-hours">{hours.map(t => <option key={t} value={t} />)}</datalist>

          {timeError && (
            <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {timeError}
            </div>
          )}

          {/* Customer */}
          <div style={{ marginBottom: '18px' }}>
            <label style={label}>Πελάτης</label>
            <CustomerField value={customer} onSelect={setCustomer} />
          </div>

          {/* Service */}
          <div style={{ marginBottom: '18px' }}>
            <label style={label}>Υπηρεσία</label>
            <FocusSelect value={serviceId} onChange={e => setServiceId(e.target.value)}>
              <option value="">Επέλεξε υπηρεσία</option>
              {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </FocusSelect>
          </div>

          {/* Status — only on edit */}
          {isEdit && (
            <div style={{ marginBottom: '18px' }}>
              <label style={label}>Κατάσταση</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                  <button
                    key={val}
                    onClick={() => setStatus(val)}
                    style={{
                      flex: '1', minWidth: '100px',
                      padding: '8px 10px',
                      borderRadius: '10px',
                      border: `1.5px solid ${status === val ? cfg.dot : '#E8E6F0'}`,
                      background: status === val ? cfg.bg : '#FAFAFA',
                      color: status === val ? cfg.color : '#888',
                      fontSize: '12px', fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    }}
                  >
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: status === val ? cfg.dot : '#ccc', flexShrink: 0 }} />
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notification toggles */}
          <div style={{
            background: '#F9F8FF',
            border: '1.5px solid #E8E6F0',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '24px',
            display: 'flex', gap: '24px',
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
              <Toggle checked={sendSms} onChange={setSendSms} color="#7C6FCD" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#1A1A2E' }}>SMS</div>
                <div style={{ fontSize: '11px', color: '#999' }}>Υπενθύμιση</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
              <Toggle checked={sendEmail} onChange={setSendEmail} color="#06b6d4" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#1A1A2E' }}>Email</div>
                <div style={{ fontSize: '11px', color: '#999' }}>Υπενθύμιση</div>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {isEdit && (
              <button
                onClick={handleDelete}
                style={{
                  padding: '11px 16px', borderRadius: '10px',
                  border: '1.5px solid #fca5a5',
                  background: '#fef2f2', color: '#dc2626',
                  fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
                </svg>
                Διαγραφή
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button
              onClick={onClose}
              style={{
                padding: '11px 20px', borderRadius: '10px',
                border: '1.5px solid #E8E6F0', background: '#F9F8FF',
                color: '#666', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Άκυρο
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '11px 24px', borderRadius: '10px',
                border: 'none', background: saving ? '#a5a0d8' : empColor,
                color: '#fff', fontSize: '13px', fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.15s',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}
            >
              {saving && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              )}
              {isEdit ? 'Αποθήκευση' : 'Καταχώριση'}
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

function Toggle({ checked, onChange, color }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: '40px', height: '22px', borderRadius: '11px',
        background: checked ? color : '#D1D5DB',
        position: 'relative', cursor: 'pointer',
        transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: '3px',
        left: checked ? '21px' : '3px',
        width: '16px', height: '16px', borderRadius: '50%',
        background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        transition: 'left 0.2s',
      }} />
    </div>
  );
}

export default AppointmentModal;
